/**
 * ============================================================
 * System       : CSAF Alert Finance
 * Objective    : Automated Reserve Fund Notification System
 * File         : frontend/src/pages/Users.jsx
 * Description  : User management CRUD
 * ------------------------------------------------------------
 * Developer    : Chokchai Thainkhao (Head of IT)
 * Co-Author    : AI Assisted (Claude) — โครงสร้างและฟังก์ชันหลัก
 * Modified &
 *  Integrated  : Chokchai Thainkhao
 * Version      : 1.0.0
 * Date         : May 2026
 * Copyright    : © 2026 Chokchai T. All rights reserved.
 * ============================================================
 */

import React, { useEffect, useState, useCallback } from 'react';
import { getUsers, createUser, updateUser, deleteUser, setUserPassword } from '../api';
import { useToast } from '../App';
import Modal from '../components/Modal';
import Paginator, { usePagination } from '../components/Paginator';

const ROLE_LABELS = { admin: 'ผู้ดูแล', user: 'ผู้ใช้', view: 'ดูอย่างเดียว' };
const ROLE_COLORS = { admin: 'bg-primary text-white', user: 'bg-blue-100 text-primary', view: 'bg-gray-100 text-gray-600' };

function initials(fname, lname) {
  return ((fname || '')[0] || '') + ((lname || '')[0] || '');
}

const EMPTY_FORM = { fname: '', lname: '', email: '', phone: '', role: 'user', dept: '', active: 1, password: '', password_confirm: '' };

export default function Users({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [pwdUser, setPwdUser] = useState(null);
  const [pwd, setPwd] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const { addToast } = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (e) {
      if (!e.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', e.response?.data?.detail || 'โหลดข้อมูลผู้ใช้ไม่สำเร็จ');
      }
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const openAdd = () => {
    setEditUser(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({
      fname: user.fname, lname: user.lname, email: user.email,
      phone: user.phone || '', role: user.role || 'user',
      dept: user.dept || '', active: user.active,
      password: '', password_confirm: '',
    });
    setModalOpen(true);
  };

  const openPwd = (user) => {
    setPwdUser(user);
    setPwd('');
    setPwdConfirm('');
  };

  const handleSave = async () => {
    if (!form.fname || !form.lname || !form.email) {
      addToast('error', 'กรุณากรอกชื่อ นามสกุล และอีเมล');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      addToast('error', 'รูปแบบอีเมลไม่ถูกต้อง');
      return;
    }
    if (!editUser && !form.password) {
      addToast('error', 'กรุณาตั้งรหัสผ่าน');
      return;
    }
    if (form.password && form.password !== form.password_confirm) {
      addToast('error', 'รหัสผ่านไม่ตรงกัน');
      return;
    }
    if (form.password && form.password.length < 4) {
      addToast('error', 'รหัสผ่านต้องมีอย่างน้อย 4 ตัวอักษร');
      return;
    }
    const payload = { ...form };
    delete payload.password_confirm;
    if (!payload.password) delete payload.password;

    setSaving(true);
    try {
      if (editUser) {
        await updateUser(editUser.id, payload);
        addToast('success', 'แก้ไขผู้ใช้งานสำเร็จ');
      } else {
        await createUser(payload);
        addToast('success', 'เพิ่มผู้ใช้งานสำเร็จ');
      }
      setModalOpen(false);
      loadData();
    } catch (err) {
      if (!err.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', err.response?.data?.detail || 'บันทึกไม่สำเร็จ');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDel) return;
    try {
      await deleteUser(confirmDel.id);
      addToast('success', 'ลบผู้ใช้งานสำเร็จ');
      setConfirmDel(null);
      loadData();
    } catch (err) {
      if (!err.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', err.response?.data?.detail || 'ลบไม่สำเร็จ');
      }
    }
  };

  const handleSetPassword = async () => {
    if (!pwd) { addToast('error', 'กรุณากรอกรหัสผ่าน'); return; }
    if (pwd !== pwdConfirm) { addToast('error', 'รหัสผ่านไม่ตรงกัน'); return; }
    if (pwd.length < 4) { addToast('error', 'รหัสผ่านต้องมีอย่างน้อย 4 ตัวอักษร'); return; }
    setPwdSaving(true);
    try {
      await setUserPassword(pwdUser.id, pwd);
      addToast('success', `ตั้งรหัสผ่านให้ ${pwdUser.fname} สำเร็จ`);
      setPwdUser(null);
    } catch (err) {
      if (!err.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', err.response?.data?.detail || 'ตั้งรหัสผ่านไม่สำเร็จ');
      }
    } finally {
      setPwdSaving(false);
    }
  };

  const filtered = users.filter((u) => {
    const companyNames = (u.companies || []).map(c => c.name).join(' ');
    const matchSearch =
      !search ||
      u.fname.includes(search) || u.lname.includes(search) ||
      u.email.includes(search) || (u.dept || '').includes(search) ||
      companyNames.includes(search);
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    const matchStatus = statusFilter === 'all' || (statusFilter === 'active' ? u.active : !u.active);
    return matchSearch && matchRole && matchStatus;
  });

  useEffect(() => { setPage(1); }, [search, roleFilter, statusFilter, pageSize]);
  const { pageItems: pageUsers, safePage } = usePagination(filtered, pageSize, page);

  const totalCount = users.length;
  const activeCount = users.filter((u) => u.active).length;
  const adminCount = users.filter((u) => u.role === 'admin').length;
  const suspendCount = users.filter((u) => !u.active).length;

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="space-y-4">
      {/* Stat cards — admin only */}
      {isAdmin && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'ทั้งหมด', value: totalCount, color: 'bg-primary text-white' },
            { label: 'ใช้งาน', value: activeCount, color: 'bg-success text-white' },
            { label: 'Admin', value: adminCount, color: 'bg-warning text-white' },
            { label: 'ระงับ', value: suspendCount, color: 'bg-danger text-white' },
          ].map((c) => (
            <div key={c.label} className={`rounded-xl p-4 ${c.color} shadow-sm`}>
              <div className="text-2xl font-bold">{c.value}</div>
              <div className="text-sm opacity-90">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="text"
          placeholder="ค้นหาชื่อ / อีเมล / แผนก / บริษัท..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        {isAdmin && (
          <>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="all">ทุกสิทธิ์</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
              <option value="view">View</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="all">ทุกสถานะ</option>
              <option value="active">ใช้งาน</option>
              <option value="inactive">ระงับ</option>
            </select>
            <button onClick={openAdd}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors">
              + เพิ่มผู้ใช้
            </button>
          </>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">ไม่พบผู้ใช้งาน</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left">ชื่อ</th>
                  <th className="px-4 py-3 text-left">อีเมล</th>
                  <th className="px-4 py-3 text-left">เบอร์</th>
                  <th className="px-4 py-3 text-left">แผนก</th>
                  <th className="px-4 py-3 text-left">บริษัท</th>
                  <th className="px-4 py-3 text-center">สิทธิ์</th>
                  <th className="px-4 py-3 text-center">สถานะ</th>
                  <th className="px-4 py-3 text-center">จัดการ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pageUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-light text-primary flex items-center justify-center text-xs font-bold shrink-0">
                          {initials(u.fname, u.lname)}
                        </div>
                        <div className="font-medium text-gray-800">{u.fname} {u.lname}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3 text-gray-600">{u.phone || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{u.dept || '-'}</td>
                    <td className="px-4 py-3 max-w-[200px]">
                      {(u.companies || []).length === 0 ? (
                        <span className="text-gray-400 text-sm">-</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(u.companies || []).map(c => (
                            <span key={c.id} className="px-2 py-0.5 bg-primary-light text-primary rounded-full text-xs font-medium truncate max-w-[130px]" title={c.name}>
                              {c.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[u.role] || ROLE_COLORS.user}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                        ${u.active ? 'bg-green-100 text-success' : 'bg-red-100 text-danger'}`}>
                        {u.active ? 'ใช้งาน' : 'ระงับ'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {/* Edit */}
                        <button onClick={() => openEdit(u)} title="แก้ไข"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-primary-light hover:text-primary transition-colors">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {/* Password */}
                        <button onClick={() => openPwd(u)} title="ตั้งรหัสผ่าน"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-yellow-50 hover:text-yellow-600 transition-colors">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                        </button>
                        {/* Delete — ซ่อนถ้าเป็นตัวเอง */}
                        {u.id !== currentUser?.id && (
                          <button onClick={() => setConfirmDel(u)} title="ลบ"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-danger transition-colors">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginator
              total={filtered.length} pageSize={pageSize} setPageSize={setPageSize}
              page={safePage} setPage={setPage}
            />
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {modalOpen && (
        <Modal
          title={editUser ? 'แก้ไขผู้ใช้งาน' : 'เพิ่มผู้ใช้งาน'}
          onClose={() => setModalOpen(false)}
          footer={
            <>
              <button onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">
                ยกเลิก
              </button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark text-sm font-medium disabled:opacity-60">
                {saving ? 'กำลังบันทึก...' : 'บันทึก'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="ชื่อจริง *" value={form.fname} onChange={(v) => setForm({ ...form, fname: v })} />
              <FormField label="นามสกุล *" value={form.lname} onChange={(v) => setForm({ ...form, lname: v })} />
            </div>
            <FormField label="อีเมล *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <div className="grid grid-cols-2 gap-4">
              <FormField label="เบอร์โทร" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <FormField label="แผนก" value={form.dept} onChange={(v) => setForm({ ...form, dept: v })} />
            </div>
            <p className="text-xs text-gray-400">กำหนดบริษัทได้จากเมนู <strong>บริษัท</strong> หลังสร้างผู้ใช้แล้ว</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">สิทธิ์</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                disabled={editUser?.id === currentUser?.id}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed">
                <option value="admin">Admin (ผู้ดูแลระบบ)</option>
                <option value="user">User (ผู้ใช้ทั่วไป)</option>
                <option value="view">View (ดูอย่างเดียว)</option>
              </select>
              {editUser?.id === currentUser?.id && (
                <p className="text-xs text-amber-500 mt-1">ไม่สามารถเปลี่ยนสิทธิ์ของตัวเองได้</p>
              )}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="text-sm font-medium text-gray-700">สถานะ:</span>
              <button type="button"
                onClick={() => setForm({ ...form, active: form.active ? 0 : 1 })}
                className={`relative inline-flex shrink-0 w-9 h-5 rounded-full transition-colors focus:outline-none ${form.active ? 'bg-success' : 'bg-gray-300'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${form.active ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
              <span className={`text-sm font-medium select-none ${form.active ? 'text-success' : 'text-gray-400'}`}>
                {form.active ? 'ใช้งาน' : 'ระงับ'}
              </span>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs text-gray-400 mb-3">
                {editUser ? 'รหัสผ่าน — เว้นว่างเพื่อไม่เปลี่ยน' : 'ตั้งรหัสผ่าน *'}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="รหัสผ่าน" type="password"
                  value={form.password} onChange={(v) => setForm({ ...form, password: v })}
                  placeholder={editUser ? '(เว้นว่าง = ไม่เปลี่ยน)' : 'อย่างน้อย 4 ตัว'} />
                <FormField label="ยืนยันรหัสผ่าน" type="password"
                  value={form.password_confirm} onChange={(v) => setForm({ ...form, password_confirm: v })}
                  placeholder="กรอกซ้ำ" />
              </div>
              {form.password && form.password_confirm && form.password !== form.password_confirm && (
                <p className="text-xs text-danger mt-1">รหัสผ่านไม่ตรงกัน</p>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Password modal */}
      {pwdUser && (
        <Modal
          title={`ตั้งรหัสผ่าน — ${pwdUser.fname} ${pwdUser.lname}`}
          onClose={() => setPwdUser(null)}
          footer={
            <>
              <button onClick={() => setPwdUser(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">
                ยกเลิก
              </button>
              <button onClick={handleSetPassword} disabled={pwdSaving}
                className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark text-sm font-medium disabled:opacity-60">
                {pwdSaving ? 'กำลังบันทึก...' : 'ตั้งรหัสผ่าน'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <FormField label="รหัสผ่านใหม่" type="password" value={pwd} onChange={setPwd} placeholder="อย่างน้อย 4 ตัวอักษร" />
            <FormField label="ยืนยันรหัสผ่าน" type="password" value={pwdConfirm} onChange={setPwdConfirm} placeholder="กรอกรหัสผ่านอีกครั้ง" />
            {pwd && pwdConfirm && pwd !== pwdConfirm && (
              <p className="text-xs text-danger">รหัสผ่านไม่ตรงกัน</p>
            )}
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {confirmDel && (
        <Modal
          title="ยืนยันการลบผู้ใช้"
          onClose={() => setConfirmDel(null)}
          footer={
            <>
              <button onClick={() => setConfirmDel(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">
                ยกเลิก
              </button>
              <button onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-danger text-white hover:bg-red-700 text-sm font-medium">
                ลบผู้ใช้
              </button>
            </>
          }
        >
          <p className="text-sm">
            คุณต้องการลบผู้ใช้ <strong>{confirmDel.fname} {confirmDel.lname}</strong> ({confirmDel.email}) ใช่หรือไม่?
          </p>
        </Modal>
      )}
    </div>
  );
}

function FormField({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={type === 'password' ? 'new-password' : 'off'}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
    </div>
  );
}
